import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type ScheduleSettingsRow = {
  id?: string;
  day_start_time: string;
  day_end_time: string;
  daily_lesson_count: number;
  effective_from: string;
  lesson_duration_minutes: number;
  break_duration_minutes: number;
  break_every_n_lessons?: number;
  lunch_start_time: string;
  lunch_duration_minutes: number;
};

export type ScheduleSettingsPreviewMeta = {
  computed_day_end: string;
  requested_lesson_count: number;
  actual_lesson_count: number;
  midnight_capped: boolean;
};

export type ScheduleSettingsResponse = {
  settings: ScheduleSettingsRow;
  slots_preview: Array<{ index: number; start: string; end: string }>;
  preview_meta: ScheduleSettingsPreviewMeta;
};

export type ScheduleSettingsImpact = {
  warning: boolean;
  effective_from: string;
  message: string;
  affected_future_appointments: number;
};

export type SuggestedEffectiveFrom = {
  suggested_effective_from: string;
  last_appointment_date: string | null;
  has_future_appointments: boolean;
  hint: string;
};

export type SaveScheduleSettingsInput = {
  branchId?: string;
  dayStartTime: string;
  dailyLessonCount: number;
  effectiveFrom: string;
  lessonDurationMinutes: number;
  breakDurationMinutes: number;
  /** 1 = her ders arasi; 2–4 = N ders sonunda birikimli mola */
  breakEveryNLessons: number;
  lunchStartTime: string;
  lunchDurationMinutes: number;
};

function timeToInput(value: string): string {
  return value.length >= 5 ? value.slice(0, 5) : value;
}

export function settingsResponseToForm(row: ScheduleSettingsRow): SaveScheduleSettingsInput {
  const everyN = Number(row.break_every_n_lessons);
  return {
    dayStartTime: timeToInput(row.day_start_time),
    dailyLessonCount: Number(row.daily_lesson_count) || 4,
    effectiveFrom: String(row.effective_from).slice(0, 10),
    lessonDurationMinutes: Number(row.lesson_duration_minutes) || 100,
    breakDurationMinutes: Number(row.break_duration_minutes) ?? 20,
    breakEveryNLessons: Number.isFinite(everyN) && everyN >= 1 && everyN <= 4 ? everyN : 1,
    lunchStartTime: timeToInput(row.lunch_start_time),
    lunchDurationMinutes: Number(row.lunch_duration_minutes) ?? 50,
  };
}

export async function fetchScheduleSettings(
  branchId?: string,
  asOfDate?: string,
): Promise<ScheduleSettingsResponse> {
  const query: Record<string, string> = {};
  if (branchId) query.branch_id = branchId;
  if (asOfDate) query.as_of_date = asOfDate;
  return api.get<ScheduleSettingsResponse>("/scheduling/settings", query);
}

export async function saveScheduleSettings(
  input: SaveScheduleSettingsInput & { branchId?: string },
): Promise<ScheduleSettingsResponse & { saved: boolean }> {
  const body: Record<string, unknown> = {
    dayStartTime: input.dayStartTime,
    dailyLessonCount: input.dailyLessonCount,
    effectiveFrom: input.effectiveFrom,
    lessonDurationMinutes: input.lessonDurationMinutes,
    breakDurationMinutes: input.breakDurationMinutes,
    breakEveryNLessons: input.breakEveryNLessons,
    lunchStartTime: input.lunchStartTime,
    lunchDurationMinutes: input.lunchDurationMinutes,
  };
  if (input.branchId) body.branchId = input.branchId;
  return api.post<ScheduleSettingsResponse & { saved: boolean }>("/scheduling/settings", body);
}

export async function fetchSuggestedEffectiveFrom(
  branchId?: string,
): Promise<SuggestedEffectiveFrom> {
  const query: Record<string, string> = {};
  if (branchId) query.branch_id = branchId;
  return api.get<SuggestedEffectiveFrom>("/scheduling/settings/suggested-effective-from", query);
}

export async function fetchScheduleSettingsImpact(
  effectiveFrom: string,
  branchId?: string,
): Promise<ScheduleSettingsImpact> {
  const query: Record<string, string> = { effective_from: effectiveFrom };
  if (branchId) query.branch_id = branchId;
  return api.get<ScheduleSettingsImpact>("/scheduling/settings/impact", query);
}

export function useScheduleSettingsImpact(
  effectiveFrom: string,
  branchId: string | undefined,
  enabled: boolean,
): {
  data: ScheduleSettingsImpact | null;
  loading: boolean;
  error: string | null;
} {
  const [data, setData] = useState<ScheduleSettingsImpact | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !effectiveFrom) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      fetchScheduleSettingsImpact(effectiveFrom, branchId)
        .then(setData)
        .catch((err) => {
          if (err instanceof ApiError) setError(err.message);
          else if (err?.name !== "AbortError") setError("Etki bilgisi alinamadi.");
        })
        .finally(() => setLoading(false));
    }, 350);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [effectiveFrom, branchId, enabled]);

  return { data, loading, error };
}

export function useScheduleSettingsLoader(branchId: string | undefined, open: boolean) {
  const [initial, setInitial] = useState<SaveScheduleSettingsInput | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchScheduleSettings(branchId);
      setInitial(settingsResponseToForm(res.settings));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Şablon Yüklenemedi.");
      setInitial(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, open]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { initial, loading, error, reload };
}
